import { useCallback } from "react";
import { useHistory } from "react-router";
import {
  ProgramData,
  IUser,
  ICertificateInfo,
  OnboardingCertificateIssueDate,
  IOptionalStandard,
  IUserProgramRolesComplianceData,
} from "@interfaces/Interfaces";
import { saveDetails, SaveDetailsParams } from "@utils/programDetails/programDetailsService";
import RemoteDataController from "@controllers/RemoteDataController";

interface UseProgramDetailsSaveReturn {
  handleSaveDetails: (
    user: IUser,
    selectedPrimaryRoles: string[],
    selectedSecondaryRoles: string[],
    selectedStandards: IOptionalStandard[],
    selectedArea: string,
    selectedTeam: string,
    appraisalDate: Date | null,
    uploads: ICertificateInfo[],
    deletions: ICertificateInfo[],
    issueDates: OnboardingCertificateIssueDate[],
    expiryDates: OnboardingCertificateIssueDate[],
    nqpCourse: string,
    userStartDate: Date | null,
    complianceData: IUserProgramRolesComplianceData[] | null
  ) => Promise<void>;
}

/**
 * Hook for saving program details
 */
export const useProgramDetailsSave = (
  program: ProgramData,
  setSaving: (saving: boolean) => void,
  setUser: (user: IUser) => void
): UseProgramDetailsSaveReturn => {
  const history = useHistory();

  const handleSaveDetails = useCallback(
    async (
      user: IUser,
      selectedPrimaryRoles: string[],
      selectedSecondaryRoles: string[],
      selectedStandards: IOptionalStandard[],
      selectedArea: string,
      selectedTeam: string,
      appraisalDate: Date | null,
      uploads: ICertificateInfo[],
      deletions: ICertificateInfo[],
      issueDates: OnboardingCertificateIssueDate[],
      expiryDates: OnboardingCertificateIssueDate[],
      nqpCourse: string,
      userStartDate: Date | null,
      complianceData: IUserProgramRolesComplianceData[] | null
    ): Promise<void> => {
      setSaving(true);

      try {
        const params: SaveDetailsParams = {
          user,
          program,
          selectedPrimaryRoles,
          selectedSecondaryRoles,
          selectedStandards,
          selectedArea,
          selectedTeam,
          appraisalDate,
          uploads,
          deletions,
          issueDates,
          expiryDates,
          nqpCourse: nqpCourse.length > 0 ? nqpCourse : undefined,
          startDate: userStartDate ? userStartDate : undefined,
          complianceData: complianceData ? complianceData : undefined,
        };

        const updatedUser = await saveDetails(params);
        setUser(updatedUser);

        history.goBack();
      } catch (error: unknown) {
        console.log(error);
        RemoteDataController.logError(error as string);
        setSaving(false);
        window.alert("An error occurred when saving details");
      } finally {
        setSaving(false);
      }
    },
    [program, setSaving, setUser, history]
  );

  return {
    handleSaveDetails,
  };
};
